import apiClient from '@/shared/services/apiClient';
import type {MembershipPlan} from '../constants/membershipPlans';

/**
 * My Labs Plus membership sign-up checkout (FE). Mirrors the shape of
 * `CheckoutService`/`BioMatchEnrollmentService` — a `createCheckoutTicket`
 * source for `PaymentWidget`'s `createIntention` prop, and a `submitCheckout`
 * that finalizes the charge + creates `user_membership` server-side.
 *
 * Backed by Task 5's `packages/api/src/controllers/membership.ts`:
 *   POST /api/membership/checkout/ticket — payment ticket intention
 *   POST /api/membership/checkout        — finalize charge + sign-up
 */

export interface MembershipCheckoutTicket {
    provider: 'fortis' | 'stripe';
    clientToken: string;
    publishableKey?: string;
}

export interface MembershipCheckoutResult {
    membershipId: number;
    /** Present for combined membership + storefront-product checkout. */
    membershipOrderId?: string;
    storefrontOrderId: string;
    /** Present for combined membership + storefront-product checkout. */
    orderNumber?: string;
    paymentStatus: 'CAPTURED' | 'PENDING' | 'FAILED';
    chargedAmountCents: number;
    plan: MembershipPlan['id'];
    currentPeriodEnd: string;
    nextChargeDate: string | null;
}

export interface SubmitMembershipCheckoutInput {
    checkoutAttemptId: string;
    plan: MembershipPlan['id'];
    ticketId: string;
    clientExpectedAmountCents: number;
    storefront?: {
        customer: {
            email: string;
            firstName: string;
            lastName: string;
            phone?: string;
            age?: number;
            state?: string;
        };
        shippingAddress: {
            line1: string;
            line2?: string;
            city: string;
            state: string;
            zip: string;
            country?: string;
        };
        billingAddress?: {
            line1: string;
            line2?: string;
            city: string;
            state: string;
            zip: string;
            country?: string;
        } | null;
        items: {productId: number; quantity: number}[];
        deliveryMethod?: 'standard' | 'express';
        promoCode?: string | null;
        giftCardCode?: string | null;
    };
}

/**
 * Caller's own membership state — mirrors the API's `MembershipState`
 * (Task 10). `plan` is loosely typed (`string`) since it's read from a
 * DB column, not re-validated to `MembershipPlan['id']` on the wire.
 */
export interface MembershipState {
    id: number;
    plan: string;
    status: string;
    amountCents: number;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    nextChargeDate: string | null;
    cancelAtPeriodEnd: boolean;
    cancelledReason: string | null;
}

export const membershipService = {
    /** Shape matches `PaymentWidget`'s `createIntention` prop exactly. */
    async createCheckoutTicket(): Promise<MembershipCheckoutTicket> {
        return apiClient.post<MembershipCheckoutTicket>('/api/membership/checkout/ticket', {});
    },

    async submitCheckout(input: SubmitMembershipCheckoutInput): Promise<MembershipCheckoutResult> {
        return apiClient.post<MembershipCheckoutResult>('/api/membership/checkout', {
            plan: input.plan,
            checkout_attempt_id: input.checkoutAttemptId,
            ticket_id: input.ticketId,
            client_expected_amount_cents: input.clientExpectedAmountCents,
            storefront: input.storefront,
        });
    },

    /** The caller's own active membership, or null if they aren't a member. */
    async getMine(): Promise<MembershipState | null> {
        const res = await apiClient.get<{membership: MembershipState | null}>('/api/membership/me');
        return res.membership;
    },

    /** Patient self-service cancel — sets `cancelAtPeriodEnd`; access is
     *  retained through `currentPeriodEnd`. Returns the updated state. */
    async cancel(reason?: string): Promise<MembershipState> {
        const res = await apiClient.post<{membership: MembershipState}>(
            '/api/membership/cancel',
            reason ? {reason} : {}
        );
        return res.membership;
    },
};

export default membershipService;
